/**
 * ProfileStep — captures what the inspector actually does, so the rest of
 * the product can personalise:
 *   - role            → which dashboard tiles surface first
 *   - regimes         → default questionnaire on /vetting/inspections/new
 *   - fleet focus     → default vessel-type filter on the map
 *   - years           → tone of help text (more verbose for newer inspectors)
 *
 * Visually: chip-style multi-selects + role cards. Continue is gated on
 * picking a role — everything else is genuinely optional.
 */
import React from 'react';
import { Check } from 'lucide-react';
import StepShell from './StepShell';
import {
  ROLES,
  REGIMES,
  FLEETS,
  Role,
  OnboardingProfile,
  StepId,
} from '../types';

interface Props {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  profile: OnboardingProfile;
  onChange: (p: OnboardingProfile) => void;
}

export default function ProfileStep({ onNext, onBack, onSkip, profile, onChange }: Props) {
  return (
    <StepShell
      step={'profile' as StepId}
      onBack={onBack}
      onNext={onNext}
      onSkip={onSkip}
      title="Tell us about your work"
      subtitle="So we can put the right vessels, the right questionnaires, and the right shortcuts in front of you. Takes 30 seconds — change anything later in Settings."
      nextDisabled={!profile.role}
    >
      <div className="space-y-8">
        {/* Role */}
        <Section
          label="What's your role?"
          required
          help="We'll surface the right dashboard tiles for each role."
        >
          <div className="grid sm:grid-cols-2 gap-2.5">
            {ROLES.map((r) => (
              <RoleCard
                key={r.id}
                role={r.id}
                label={r.label}
                sub={r.sub}
                selected={profile.role === r.id}
                onClick={() => onChange({ ...profile, role: r.id })}
              />
            ))}
          </div>
        </Section>

        {/* Regime expertise */}
        <Section
          label="Which regimes do you work with?"
          help="Select all that apply. We'll preload questionnaires you actually use."
        >
          <div className="flex flex-wrap gap-2">
            {REGIMES.map((reg) => {
              const sel = profile.regimes.includes(reg);
              return (
                <Chip
                  key={reg}
                  selected={sel}
                  onClick={() =>
                    onChange({
                      ...profile,
                      regimes: sel
                        ? profile.regimes.filter((x) => x !== reg)
                        : [...profile.regimes, reg],
                    })
                  }
                >
                  {reg}
                </Chip>
              );
            })}
          </div>
        </Section>

        {/* Fleet focus */}
        <Section
          label="Which vessel types do you focus on?"
          help="Sets the default filter when you open the live map."
        >
          <div className="flex flex-wrap gap-2">
            {FLEETS.map((f) => {
              const sel = profile.fleets.includes(f.id);
              return (
                <Chip
                  key={f.id}
                  selected={sel}
                  onClick={() =>
                    onChange({
                      ...profile,
                      fleets: sel
                        ? profile.fleets.filter((x) => x !== f.id)
                        : [...profile.fleets, f.id],
                    })
                  }
                >
                  {f.label}
                </Chip>
              );
            })}
          </div>
        </Section>

        {/* Years experience */}
        <Section
          label="Years of vetting / inspection experience"
          help="We'll keep help text proportional — more detail for newer inspectors, terser for veterans."
        >
          <div className="rounded-xl bg-slate-900/60 border border-slate-800 px-5 py-4">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-3xl font-bold text-cyan-300">{profile.yearsExperience}</span>
              <span className="text-xs text-slate-500 uppercase tracking-wider">
                {profile.yearsExperience === 0 ? 'Just starting'
                : profile.yearsExperience < 3   ? 'Early career'
                : profile.yearsExperience < 10  ? 'Experienced'
                : profile.yearsExperience < 20  ? 'Senior'
                                                : 'Master'}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={30}
              value={profile.yearsExperience}
              onChange={(e) => onChange({ ...profile, yearsExperience: Number(e.target.value) })}
              className="w-full accent-cyan-400"
            />
            <div className="mt-1 flex justify-between text-[10px] text-slate-500 uppercase tracking-wider">
              <span>0</span><span>10</span><span>20</span><span>30+</span>
            </div>
          </div>
        </Section>
      </div>
    </StepShell>
  );
}

function Section({
  label,
  help,
  required,
  children,
}: {
  label: string;
  help?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-3">
        <h3 className="text-sm font-bold text-white inline-flex items-center gap-1.5">
          {label}
          {required && (
            <span className="text-[9px] uppercase tracking-wider text-amber-300 font-bold border border-amber-500/40 bg-amber-500/10 rounded px-1.5 py-0.5">
              Required
            </span>
          )}
        </h3>
        {help && <p className="text-xs text-slate-500 mt-1">{help}</p>}
      </div>
      {children}
    </div>
  );
}

function RoleCard({
  role,
  label,
  sub,
  selected,
  onClick,
}: {
  role: Role;
  label: string;
  sub: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl px-4 py-3.5 border transition-all duration-150 active:scale-[0.99] ${
        selected
          ? 'bg-cyan-500/10 border-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.25)]'
          : 'bg-slate-900/60 border-slate-800 hover:border-slate-700 hover:bg-slate-900/80'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className={`text-sm font-bold ${selected ? 'text-cyan-200' : 'text-white'}`}>{label}</div>
          <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
        </div>
        {selected && (
          <Check className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
        )}
      </div>
    </button>
  );
}

function Chip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 active:scale-[0.97] ${
        selected
          ? 'bg-cyan-500/15 border-cyan-400 text-cyan-200'
          : 'bg-slate-900/60 border-slate-700 text-slate-300 hover:border-slate-600'
      }`}
    >
      {selected && <Check className="w-3 h-3" />}
      <span>{children}</span>
    </button>
  );
}
